/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React, { useContext, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { AIProtonModel } from "@NA/Widgets/AIProton/AIProtonModel";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import Img from "@insite/mobius/Img";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";

function hasEnoughInventory(state: ApplicationState, product: AIProtonModel, productInfo: ProductInfo) {
    const { inventory, unitOfMeasure } = productInfo;
    const { fulfillmentMethod } = getSession(state);
    const { productSettings } = getSettingsCollection(state);
    const allowBackOrder =
        fulfillmentMethod === FulfillmentMethod.PickUp
            ? productSettings.allowBackOrderForPickup
            : productSettings.allowBackOrderForDelivery;
    const inventoryAvailability = inventory?.inventoryAvailabilityDtos?.find(o => o.unitOfMeasure === unitOfMeasure);

    return (
        allowBackOrder ||
        product.isVariantParent ||
        !product.trackInventory ||
        (inventoryAvailability?.availability &&
            inventoryAvailability.availability.messageType !== AvailabilityMessageType.OutOfStock)
    );
}

function canAddToCart(
    state: ApplicationState,
    product: AIProtonModel,
    productInfo?: ProductInfo,
    configurationCompleted?: boolean,
    variantSelectionCompleted?: boolean,
) {
    if (!product) {
        return false;
    }

    const { allowBackOrder } = getSettingsCollection(state).productSettings;
    const availability =
        productInfo?.inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (productInfo?.unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;
    return (
        !product.cantBuy &&
        (product.canAddToCart ||
            ((configurationCompleted || variantSelectionCompleted) &&
                (allowBackOrder || (availability && availability.messageType !== 2)) &&
                !product.canConfigure))
    );
}

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    product: AIProtonModel;
    title: string;
}
export interface ProductCarouselProductStyles {
    addToCartButton?: InjectableCss;
}
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomApplicationState, props: OwnProps) => {
    const productInfo: ProductInfo = {
        productId: props.product.id,
        qtyOrdered: props.product.qtyOrdered,
        unitOfMeasure: props.product.unitOfMeasure,
        productDetailPath: props.product.productDetailUrl,
    };
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.product,
            productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.product, productInfo),
        addingProductToCart: state.context.isUpdatingCart,
        addToCartLoading: state.context.isUpdatingCart,
        showADIAddToCart: (adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess,
    };
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const AIProductAddToCartButton = ({
    productSettings,
    hasEnoughInventory,
    addingProductToCart,
    canAddToCart,
    addToCartLoading,
    labelOverride,
    extendedStyles,
    notes,
    product,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    showADIAddToCart,
    title,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [selectedProduct, setSelectedProduct] = useState("");
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const dispatch: any = useDispatch();

    if (!productSettings.canAddToCart || !hasEnoughInventory || !canAddToCart || !showADIAddToCart) {
        return null;
    }

    const imageStyle = {
        css: css`
            > Img {
                width: 40px;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                position: absolute;
            }
        `,
    };

    const buttonHeight = {
        css: css`
            height: 63px;
            min-height: 63px;
            width: 100%;
            border: 0;
            border-top: 1px solid ${getColor("text.disabled")};
            text-align: center;
            padding: 20px 0;
            cursor: pointer;
            transition: 0.2s ease;
            margin-top: auto;
            border-radius: 0;
            border-bottom-left-radius: 5px;
            border-bottom-right-radius: 5px;
            font-size: 16px;
            font-weight: 700;
            position: relative;
            background: ${getColor("primary.contrast")};
            :disabled {
                background: ${getColor("primary.contrast")};
                cursor: not-allowed;
                border-top: 1px solid ${getColor("text.disabled")} !important;
            }
            ${TypographyStyle} {
                color: ${getColor("primary")};
                font-size: 16px;
                font-weight: 700;
            }
            :hover {
                background-color: ${getColor("primary")};
                border-top: 1px solid ${getColor("primary")};
                font-size: 16px;
                font-weight: 700;
                ${TypographyStyle} {
                    color: ${getColor("primary.contrast")};
                }
            }
            :active {
                background-color: ${getColor("secondary")};
                border-top: 1px solid ${getColor("secondary")};
                font-size: 16px;
                font-weight: 700;
                ${TypographyStyle} {
                    color: ${getColor("primary.contrast")};
                }
            }
            :focus {
                outline: 0;
            }
        `,
    };

    const addToCartClickHandler = async (modelNumber: string) => {
        setSelectedProduct(modelNumber);
        if (
            !product.quoteRequired &&
            !product.allowZeroPricing &&
            product.pricing &&
            getUnitNetPrice(product.pricing, 1).price === 0
        ) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        sessionStorage.setItem("item_list_name", title);
        dispatch(
            addToCart({
                productId: product.id,
                qtyOrdered: 1,
                unitOfMeasure: product.unitOfMeasure,
                notes,
                sectionOptions,
                subscription,
                onComplete() {
                    setSelectedProduct("");
                },
            }),
        ) as any;

        // if (productSettings.showAddToCartConfirmationDialog) {
        //     toasterContext.addToast({
        //         body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
        //         messageType: "success",
        //     });
        // }
    };

    return (
        <Button
            className="addToCartButton"
            {...buttonHeight}
            disabled={addToCartLoading}
            onClick={() => addToCartClickHandler(product.modelNumber)}
            {...otherProps}
        >
            {addToCartLoading && selectedProduct === product.modelNumber ? (
                <StyledWrapper {...imageStyle}>
                    {/* added hardcode image for image will move it to static folder and access it from there */}
                    <Img
                        alt="loader"
                        src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/rd-addtocart-loader.gif"
                    />
                </StyledWrapper>
            ) : (
                labelOverride ?? translate("Add to Cart")
            )}
        </Button>
    );
};
export default connect(mapStateToProps)(AIProductAddToCartButton);
